import { format } from 'date-fns';
import { Answer } from 'types/answer';
import { RCLeagueSeason } from 'types/coreEntites/Leagues';
import { EventAsSeasonSegment } from 'types/events';
import { Product, ProductPrice } from 'types/product';
import { ProgramProductInCart } from 'types/program';
import { rcEnums } from 'types/rc.enums';
import { ResourcesToProduct } from 'types/resources-to-products';
import { RegistrationWindowStatusEnum, Season, SeasonAsSegment } from 'types/seasons';
import { UserSettings } from 'types/userSettings';
import { IProductResource } from 'types/product-resource';
import { getDatesRange, getEventDate } from './dates';
import { isLater, TimeUnit } from '@bondsports/date-time';
import { IPricesOfProductsResults, ResourceNameTypeEnum } from '@bondsports/types';
import { validateAmount } from '@bondsports/general';
import { ProductInCart, ProductInCartEnum } from 'types/cart/productInCart';

export const sortPricesByStartDate = (a: ProductPrice, b: ProductPrice) => a.startDate.localeCompare(b.startDate);

const EARLY_BIRD_PRICE = 'Early Bird Price';
const LATE_FEE_PRICE = 'Late Fee Price';

export const showEarlyBird = (product: Product | null) => {
	if (!product) {
		return false;
	}

	// you need at least 2 prices to have an early bird
	if (!product.prices || !Array.isArray(product.prices) || product.prices.length < 2) {
		return false;
	}

	if (product.prices.length < 2) {
		return false;
	}

	const sortedPrices = [...product.prices];

	sortedPrices.sort(sortPricesByStartDate);

	const today = format(new Date(), 'yyyy-MM-dd');

	// first price is assumed to be early bird
	const first = sortedPrices[0];

	// if the first date (of more than 1) is active - we're in early bird !
	if (first.startDate <= today && today <= first.endDate) {
		return true;
	}

	return false;
};

export interface CartItemProps {
	previousCartItems: ProgramProductInCart[];
	program: string;
	product: Product;
	season: Season | RCLeagueSeason;
	member: UserSettings | undefined | UserSettings[];
	resources: ResourcesToProduct | undefined;
	addons: Product[];
	sport: rcEnums.shared.SportsEnum;
	answers: Answer[] | { userId: number; answers: Answer[] }[];
}

export const getCalculatedProduct = (
	selectedProduct: Product,
	calcPriceResponse: IPricesOfProductsResults[],
	memberId: number
) => {
	const newProduct = JSON.parse(JSON.stringify(selectedProduct));
	const calculatedProduct = calcPriceResponse?.find(r => r.userId === memberId && r.productId === selectedProduct.id);
	const newPrice = calculatedProduct?.unitPrice;
	if (validateAmount(newPrice as number)) {
		newProduct.currPrice.originalPrice = newProduct.currPrice.price;
		newProduct.currPrice.price = newPrice;
	}
	return newProduct;
};
// TBD: refactor this function for cart servise- remove ordinal
// convert a single product with its addons to cart items
export function productAndAddonsToCartItems({
	previousCartItems,
	program,
	product,
	season,
	member,
	resources,
	addons,
	sport,
	answers,
}: CartItemProps): ProgramProductInCart[] {
	let nextId =
		previousCartItems && previousCartItems.length ? previousCartItems[previousCartItems.length - 1].ordinal + 1 : 1;

	const parentOrdinal = nextId;
	const prodToCart: ProgramProductInCart[] = [
		{
			ordinal: nextId,
			type: ProductInCartEnum.PACKAGE,
			program,
			product,
			season: season as Season,
			member: member as UserSettings,
			addons, // this is here just for
			children: addons.map(addon => ({
				season: season as Season,
				ordinal: parentOrdinal,
				type: ProductInCartEnum.ADDON,
				quantity: addon.quantity as unknown as number,
				resourceType: ResourceNameTypeEnum.EVENT,
				member: member as UserSettings,
				product: addon,
				resources: addon.resources as ResourcesToProduct,
			})) as unknown as ProductInCart[],
			resources,
			answers,
			sport,
		},
	];
	for (const addon of addons) {
		if (!addon) {
			continue;
		}
		nextId++;
		prodToCart.push({
			ordinal: nextId,
			parentOrdinal,
			type: ProductInCartEnum.ADDON,
			program,
			product: addon,
			season: season as Season,
			resources: addon.resources as ResourcesToProduct,
			// resources, no resources for addon - at least for now
			member: member as UserSettings,
		});
	}

	return prodToCart;
}

// convert a Product to ProgramProductInCart
export const productToProductCart = (
	product: IProductResource,
	member: UserSettings,
	season: Season,
	productsNum: number
): ProgramProductInCart => {
	if (Array.isArray(product.resources)) {
		if (!product.resources || !product.resources[0].resources[0]) {
			throw new Error('cannot convert a product with no resource');
		}
	} else {
		if (!product.resources || !product.resources.resources[0]) {
			throw new Error('cannot convert a product with no resource');
		}
	}

	return {
		type: ProductInCartEnum.PACKAGE,
		product: product as Product,
		season: season,
		program: product.description || '',
		ordinal: productsNum + 1,
		member, // the selected member
		resources: product.resources,
	};
};

export const isEarlyBird = (season: Season, price?: ProductPrice): boolean => {
	// has to have at least 1 product
	if (!season.products || !(season.products as Product[]).length) {
		return false;
	}

	// has to have at more than 1 price
	const mainProduct = (season.products as Product[])[0];
	if (!mainProduct.prices || mainProduct.prices?.length < 2) {
		return false;
	}

	// either check the requested price, or the first price DATE wise
	const priceToCheck = price ?? mainProduct.prices.sort((p1, p2) => p1.startDate.localeCompare(p2.startDate))[0];

	// current price is the first
	return mainProduct.currPrice?.id === priceToCheck.id && mainProduct.currPrice?.name === EARLY_BIRD_PRICE;
};

export const isLateFeePrice = (season: Season, price?: ProductPrice): boolean => {
	if (!season.products || !(season.products as Product[]).length) {
		return false;
	}

	const mainProduct = (season.products as Product[])[0];
	if (!mainProduct.prices || mainProduct.prices?.length < 2) {
		return false;
	}

	price ??= mainProduct.currPrice;

	return price?.name === LATE_FEE_PRICE;
};

export type SubSeasonItem = SeasonAsSegment | EventAsSeasonSegment;

// helper functions to parse sub season to string
export const getSubSeasonName = (seasonItem: SubSeasonItem): string => {
	return seasonItem.segmentType === rcEnums.shared.ResourceNameTypeEnum.PROGRAM_SEASON
		? (seasonItem as SeasonAsSegment).name
		: (seasonItem as EventAsSeasonSegment).title;
};

export const getSubSeasonDateRange = (seasonItem: SubSeasonItem): string => {
	return seasonItem.segmentType === rcEnums.shared.ResourceNameTypeEnum.PROGRAM_SEASON
		? getDatesRange(seasonItem.startDate, seasonItem.endDate)
		: getEventDate((seasonItem as EventAsSeasonSegment).startDateString);
};

export const getIsPastSeason = (selectedSeason?: RCLeagueSeason) => {
	if (selectedSeason?.lateRegistrationEnds) {
		return isLater(new Date(), selectedSeason?.lateRegistrationEnds, TimeUnit.DAY);
	}
	if (selectedSeason?.regularRegistrationEnds) {
		return isLater(new Date(), selectedSeason?.regularRegistrationEnds, TimeUnit.DAY);
	}
	if (selectedSeason?.earlyRegistrationEnds) {
		return isLater(new Date(), selectedSeason?.earlyRegistrationEnds, TimeUnit.DAY);
	}
};

export const isRegistrationWindowOpenToAny = (
	subSeasons: { registrationWindowStatus?: RegistrationWindowStatusEnum }[]
) => {
	return (
		subSeasons?.some(
			segmentOrEvent =>
				!segmentOrEvent.registrationWindowStatus ||
				segmentOrEvent.registrationWindowStatus === RegistrationWindowStatusEnum.OPEN
		) ?? false
	);
};

// checks if a season is a future season
export const futureSeason = (season: RCLeagueSeason | Season) => {
	const currentTime = new Date();

	return new Date(season.startDate) >= currentTime && new Date(season.endDate) >= currentTime;
};

// check if a season is active season
export const activeSeason = (season: RCLeagueSeason | Season) => {
	const currentTime = new Date();

	return new Date(season.startDate) <= currentTime && new Date(season.endDate) >= currentTime;
};

// check if a season is past season
export const pastSeason = (season: RCLeagueSeason | Season) => {
	const currentTime = new Date();

	return new Date(season.endDate) < currentTime;
};
